import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryName } from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOHead from '@/components/base/SEOHead';
import { allArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const catName = getCategoryName(article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '安全指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://safety-guide.example.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://safety-guide.example.com' },
      { '@type': 'ListItem', position: 2, name: catName, item: `https://safety-guide.example.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 成人网站安全使用完全指南`}
        description={article.excerpt}
        keywords={`成人网站,${article.tags.join(',')},成人网站安全访问,成人网站隐私保护`}
        canonicalUrl={`https://safety-guide.example.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        schemaJson={articleSchema}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <article>
            <header className="relative w-full h-[320px] md:h-[440px] flex items-end overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/45" />
              <div className="relative z-10 w-full px-4 md:px-6 max-w-3xl mx-auto pb-10 md:pb-14">
                <div className="flex items-center gap-2 mb-3">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap"
                  >
                    {catName}
                  </Link>
                  <span className="text-xs text-white/70">{article.date}</span>
                </div>
                <h1 className="text-xl md:text-3xl font-heading font-bold text-white leading-tight mb-3">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-xs text-white/70">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line" />
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" />
                    {article.readTime}
                  </span>
                </div>
              </div>
            </header>

            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
              <div className="py-6">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    { label: catName, path: `/category/${article.category}` },
                    { label: article.title },
                  ]}
                />
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="pb-14 md:pb-20">
                <div className="prose-custom text-foreground-800 leading-relaxed">
                  {article.content.map((block, index) => {
                    if (block.type === 'h2') {
                      return (
                        <h2 key={index} className="text-xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                          {block.text}
                        </h2>
                      );
                    }
                    if (block.type === 'h3') {
                      return (
                        <h3 key={index} className="text-lg font-heading font-semibold text-foreground-900 mt-8 mb-3">
                          {block.text}
                        </h3>
                      );
                    }
                    return (
                      <p key={index} className="text-base text-foreground-700 leading-loose mb-5">
                        {block.text}
                      </p>
                    );
                  })}
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-14 md:py-20 bg-background-100">
              <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-8">
                  相关文章推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.slug} {...ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}